import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, Edit, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, Dialog, Input } from '@/design-system';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { companyDeletionConfirmation, companyDeletionsService } from '@/features/company-deletions';
import { companiesService, type UpdateCompanyData } from '../api/companies.service';
import { useCompanyDetails } from '../company-details-context';

const fields = [
  ['name', 'Nome (Razão social)'], ['fantasyName', 'Nome fantasia'], ['document', 'Documento'],
  ['email', 'Email'], ['phone', 'Telefone'], ['mobilePhone', 'Celular'],
  ['addressLine1', 'Endereço'], ['addressLine2', 'Complemento / bairro'], ['postalCode', 'CEP'],
  ['city', 'Cidade'], ['stateOrProvince', 'Estado'], ['country', 'País'],
  ['municipalInscription', 'Inscrição municipal'], ['stateInscription', 'Inscrição estadual'],
  ['defaultLanguage', 'Idioma'], ['timezone', 'Fuso horário'],
] as const;
type Field = typeof fields[number][0];
type CompanyForm = Record<Field, string>;
type Action = 'delete' | 'archive' | 'restore';

/**
 * Uses the same lifecycle endpoints as the company list; never purges on a button click alone.
 *
 * - Arquivar  → PRESERVE_DATA: desativa a empresa e mantém os dados (reversível, qualquer perfil que edita).
 * - Excluir   → DELETE_ALL_DATA: apaga todos os dados, irreversível (somente SUPERADMIN).
 */
export function CompanyDetailsActions() {
  const { company, staffUser, isPurged, hasFinancialArchiveInOrya, setShowEraseFinancialDialog, setShowDestroyShellDialog } = useCompanyDetails();
  const qc = useQueryClient();
  const onError = useErrorToast();
  const [form, setForm] = useState<CompanyForm | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const isAdmin = staffUser?.role === 'SUPERADMIN';
  const canEdit = ['SUPERADMIN', 'FINANCE', 'SALES', 'SUPPORT'].includes(staffUser?.role ?? '');
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.companies.all });
  const save = useMutation({
    mutationFn: (data: UpdateCompanyData) => companiesService.update(company.id, data),
    onSuccess: async () => { setForm(null); await refresh(); toast.success('Empresa atualizada.'); },
    onError: (error) => onError(error, 'Não foi possível atualizar a empresa'),
  });
  const lifecycle = useMutation({
    mutationFn: async (target: Action): Promise<void> => {
      if (target === 'restore') return companiesService.restore(company.id);
      await companyDeletionsService.request(company.id, target === 'archive' ? 'PRESERVE_DATA' : 'DELETE_ALL_DATA');
    },
    onSuccess: async (_, target) => {
      setAction(null);
      await refresh();
      if (target !== 'restore') await qc.invalidateQueries({ queryKey: queryKeys.companyDeletions.all });
      toast.success(target === 'restore' ? 'Empresa restaurada.' : `${target === 'archive' ? 'Arquivamento' : 'Exclusão'} solicitado. Acompanhe em Ferramentas › Exclusões de empresas.`);
    },
    onError: (error) => onError(error, 'Não foi possível concluir a ação na empresa'),
  });
  if (!canEdit) return null;
  const confirmation = action === 'restore'
    ? { title: 'Restaurar empresa', text: 'Restaurar', message: `Restaurar "${company.name}" e permitir seu acesso novamente?` }
    : (() => {
      const { title, confirmText, message } = companyDeletionConfirmation(action === 'archive' ? 'PRESERVE_DATA' : 'DELETE_ALL_DATA', company.name);
      return { title, text: confirmText, message };
    })();
  return <>
    {!isPurged && <>
      <Button variant="subtle" onClick={() => setForm(Object.fromEntries(fields.map(([field]) => [field, company[field] ?? ''])) as CompanyForm)} disabled={lifecycle.isPending}><Edit size={16} />Editar</Button>
      {company.deletedAt
        ? <Button variant="subtle" onClick={() => setAction('restore')} disabled={lifecycle.isPending}><RotateCcw size={16} />Restaurar</Button>
        : <Button variant="subtle" onClick={() => setAction('archive')} disabled={lifecycle.isPending}><Archive size={16} />Arquivar</Button>}
      {isAdmin && <Button variant="danger" onClick={() => setAction('delete')} disabled={lifecycle.isPending}><Trash2 size={16} />Excluir</Button>}
    </>}
    {isPurged && isAdmin && <>
      {hasFinancialArchiveInOrya && <Button variant="subtle" onClick={() => setShowEraseFinancialDialog(true)}>Apagar histórico financeiro…</Button>}
      <Button variant="danger" onClick={() => setShowDestroyShellDialog(true)}><Trash2 size={16} />Excluir definitivamente do Orya…</Button>
    </>}
    <Dialog isOpen={!!form} onClose={() => { if (!save.isPending) setForm(null); }} title="Editar empresa" size="lg" footer={<>
      <Button variant="subtle" onClick={() => setForm(null)} disabled={save.isPending}>Cancelar</Button>
      <Button type="submit" form="edit-company-details" isLoading={save.isPending}>Salvar alterações</Button>
    </>}>
      {form && <form id="edit-company-details" className="grid gap-4 sm:grid-cols-2" onSubmit={(event) => {
        event.preventDefault();
        if (!form.name.trim() || form.name.trim().length < 2) return;
        const changes = Object.fromEntries(fields.filter(([field]) => form[field] !== (company[field] ?? '')).map(([field]) => [field,
          field === 'name' ? form.name.trim() : form[field] || (field === 'defaultLanguage' || field === 'timezone' ? undefined : null),
        ])) as UpdateCompanyData;
        if (['addressLine1', 'addressLine2', 'postalCode', 'city', 'stateOrProvince', 'country'].some((field) => field in changes)) {
          changes.formattedAddress = null;
          changes.placeId = null;
        }
        save.mutate(changes);
      }}>
        {fields.map(([field, label]) => <Input key={field} label={label} value={form[field]} required={field === 'name'} minLength={field === 'name' ? 2 : undefined}
          type={field === 'email' ? 'email' : 'text'} disabled={save.isPending} onChange={(event) => setForm({ ...form, [field]: event.target.value })} />)}
      </form>}
    </Dialog>
    <ConfirmDialog isOpen={!!action} onClose={() => { if (!lifecycle.isPending) setAction(null); }}
      onConfirm={() => { if (action) lifecycle.mutate(action); }} title={confirmation.title} message={confirmation.message}
      confirmText={confirmation.text} variant={action === 'restore' ? 'default' : 'danger'} isLoading={lifecycle.isPending} />
  </>;
}
